import { GtmFactory, CHECKOUT_STEP, PAYMENT_TYPE } from "./gtm_script";
import { addCorrId, tapAndLoadCorrId } from "./utils/correlation_id_store";
import {
  getFormattedCurrencyValueFromEl,
  formatterCurrency,
  getLowerCaseInputValue
} from "./formatters";
import {
  isPJ,
  doesNotHaveValue,
  parseFetchResponse,
  checkoutCustomerFormHelpers,
  disableSubmitButtonElement,
  setElementHiddenClass
} from "./helper_functions";
import { couponFormFactory } from "./coupon_form_factory";
import { webhook } from "./webhook_service";
import { debounced } from "./debounced";
import { getDescriptiveTime } from "./utils/get_descriptive_time";
import { getPaymentInfoHelpers } from "./utils/form/payment/payment_form";
import { useRecaptcha } from "./utils/recaptcha_wrapper";
import { getUTMParams } from './utils/cookie_manager';
import { purchaseCallbackFor } from "./utils/gtm_callback";

document.addEventListener("DOMContentLoaded", function () {
  let paymentIntentId = null;
  const PIX_STATUS_POLLING_TIME_LIMIT = window.PM3.PIX_STATUS_POLLING_TIME_LIMIT || 1000 * 60 * 5; // 5 min in ms
  const PIX_STATUS_POLLING_INTERVAL = window.PM3.PIX_STATUS_POLLING_INTERVAL || 1000 * 5; // 5s in ms
  const PIX_EXPIRATION_UPDATE_INTERVAL = window.PM3.PIX_EXPIRATION_UPDATE_INTERVAL || 60 * 1000;
  const gtm = GtmFactory();

  // Product details
  const productIdHiddenInput = document.getElementById("product_id");
  const productNameHiddenInput = document.getElementById("product_name");
  const productUnitAmountHiddenInput =
    document.getElementById("unit-amount") ||
    document.getElementById("payment-total");
  const productTypPixProcessing = document.getElementById("typ_pix_processing");
  const productTypPixError = document.getElementById("typ_pix_error");

  // Defines TYP pix
  const typPixProcessing =
    productTypPixProcessing?.value?.length > 0
      ? productTypPixProcessing.value
      : window.PM3.typPixProcessing;
  const typPixError = productTypPixError?.value?.length
    ? productTypPixError.value
    : window.PM3.typPixError;

  // Defines PF or PJ from backend
  const formClientType = document.getElementById("client_payment_client_type");

  // form
  const formElement = document.getElementById("client_payment_form");
  const formNameInput = document.getElementById("client_payment_name");
  const formEmailInput = document.getElementById("client_payment_email");
  const formDocumentInput = document.getElementById("client_payment_document");
  const isForeignerInput = document.getElementById("is_foreigner");
  const formPhoneNumberInput = document.getElementById(
    "client_payment_phone_number"
  );
  const formZipCodeInput = document.getElementById("client_payment_zip_code");
  const whatsappAcceptElement = document.getElementById("whatsapp_checkbox");
  // CNPJ registration
  const stateRegistrationInput = formElement.querySelector(
    "input#client_payment_state_registration"
  );
  const municipalRegistrationInput = formElement.querySelector(
    "input#client_payment_municipal_registration"
  );

  const productProviderHiddenInput = document.getElementById("provider");
  const formCouponIdHiddenInput = document.getElementById(
    "client_payment_coupon_id"
  );
  const formAmountOffHiddenInput = document.getElementById(
    "client_payment_amount_off"
  );
  const formPercentOffHiddenInput = document.getElementById(
    "client_payment_percent_off"
  );
  const formDiscountAmountHiddenInput = document.getElementById(
    "client_payment_discount_amount"
  );
  const formAmountHiddenInput = document.getElementById(
    "client_payment_amount"
  );
  const formTotalAmountHiddenInput = document.getElementById(
    "client_payment_total_amount"
  );

  const stateRegistrationAlertMessageElement = document.querySelector(
    "#error_registration"
  );
  const formDocumentAlertMessageSpan = document.querySelector(
    "#document_validation"
  );
  const formZipCodeAlertMessageSpan = document.querySelector(
    "#zip_code_validation"
  );

  // UI Form elements
  const whatsappLabelElement = document.getElementById("whatsapp_label");
  const documentLabelElement = document.getElementById("document_label");
  const zipCodeLabelElement = document.getElementById("zip_code_label");

  /** @type {HTMLButtonElement} */
  const submitButtonElement = document.querySelector("#submit");

  /** @type {HTMLDivElement} */
  const buttonLoadingSpinner = submitButtonElement.querySelector(
    "#pay-now-button-spinner"
  );
  /** @type {HTMLSpanElement} */
  const payNowButtonLabel = submitButtonElement.querySelector(
    "#pay-now-button-label"
  );

  const formHelpers = checkoutCustomerFormHelpers({
    alertMessageDocumentElement: formDocumentAlertMessageSpan,
    documentLabelElement,
    formDocumentElement: formDocumentInput,
    formPhoneNumberElement: formPhoneNumberInput,
    formClientType,
    isForeignerElement: isForeignerInput,
    submitButtonElement,
    whatsappAcceptElement,
    whatsappLabelElement,
    alertMessageZipCodeElement: formZipCodeAlertMessageSpan,
    formZipCodeElement: formZipCodeInput,
    zipCodeLabelElement
  });

  // Coupon Form init
  if (window.coupon_accepted) {
    const couponElements = {
      formAmountHiddenInput,
      couponInput: document.getElementById("coupon_id"),
      discountAmountSpan: document.getElementById("discount-amount"),
      paymentTotalSpan: document.getElementById("payment-total"),
      clearCouponButton: document.getElementById("clear_coupon"),
      errorSpanContainer:
        document.getElementById("coupon-container")?.parentElement,
      submitCouponButton: document.getElementById("submit_coupon"),
      pixTotalAmountSpans: document.querySelectorAll("#pix_total_amount"),
      emailInput: formEmailInput,
      submitFormButton: submitButtonElement
    };

    let timeoutRef = null;

    const couponForm = couponFormFactory({
      elements: couponElements,
      productId: productIdHiddenInput.value
    });
    if (couponForm && !couponForm.fixedCoupon) {
      /**
       * Update pix price
       * @param {string} amount numeric value
       */
      const updatePixPrice = (amount) => {
        couponElements.pixTotalAmountSpans.forEach(function(span) {
          span.innerText = ` ${amount}`;
        });
      };
      /**
       * Handles changes in coupon values
       *
       * @param {{
       *   amount: string
       *   amountOff: number
       *   coupon: string
       *   discountAmount: number
       *   discountedAmount: number
       *   formattedDiscount: string
       *   formattedDiscountedAmount: string
       *   percentOff: string
       * } | null} coupon
       * @param {boolean=} clear
       */
      const couponChangeHandler = (coupon, clear = false) => {
        const rawOriginalAmount = Number(formAmountHiddenInput.value) / 100;
        const originalAmount = formatterCurrency.format(rawOriginalAmount);
        if (clear) {
          formCouponIdHiddenInput.value = "";
          formTotalAmountHiddenInput.value = formAmountHiddenInput.value;
          formDiscountAmountHiddenInput.value = "";
          formAmountOffHiddenInput.value = "";
          formPercentOffHiddenInput.value = "";

          couponElements.paymentTotalSpan.innerText = originalAmount;
          couponElements.discountAmountSpan.innerText = "";

          updatePixPrice(originalAmount);
          return;
        } else if (coupon === null && formCouponIdHiddenInput.value) {
          if (timeoutRef !== null) {
            clearTimeout(timeoutRef);
          }
          timeoutRef = setTimeout(() => {
            couponForm.removeErrorSpan();
          }, 3000);
          return;
        } else if (coupon === null) {
          couponForm.appendErrorSpan();
          return;
        }

        couponForm.removeErrorSpan();

        formAmountOffHiddenInput.value = coupon.amountOff;
        formCouponIdHiddenInput.value = coupon.coupon;
        formDiscountAmountHiddenInput.value = coupon.discountAmount;
        formTotalAmountHiddenInput.value = coupon.discountedAmount;
        formPercentOffHiddenInput.value = coupon.percentOff;

        couponElements.paymentTotalSpan.innerText =
          coupon.formattedDiscountedAmount;
        couponElements.discountAmountSpan.innerText = coupon.formattedDiscount;

        updatePixPrice(coupon.formattedDiscountedAmount);
      };
      couponForm.addCouponChangeListener(couponChangeHandler);
    }
  }
  // END of Coupon Form

  /**
   * Show a spinner on payment submission
   *
   * @param {boolean} isLoading
   */
  const setLoading = function (isLoading, elements = {}) {
    const { loading = buttonLoadingSpinner, label = payNowButtonLabel } = elements;
    // Disable the button and show the spinner
    disableSubmitButtonElement(submitButtonElement);

    setElementHiddenClass(loading, !isLoading);
    setElementHiddenClass(label, isLoading);
  };

  // Show the customer the error from Stripe if their card fails to charge
  const showPaymentError = function (errorMsgText) {
    setLoading(false);
    const errorMsg = document.querySelector("#card-error");
    errorMsg.textContent = errorMsgText;
  };

  // Get payment information from form
  const getAmountAndCouponInformation = () => ({
    coupon_id: formCouponIdHiddenInput.value,
    coupon_amount_off: getFormattedCurrencyValueFromEl(
      formAmountOffHiddenInput
    ),
    coupon_percent_off: formPercentOffHiddenInput.value,
    discount_amount: getFormattedCurrencyValueFromEl(
      formDiscountAmountHiddenInput
    ),
    subtotal_amount: productUnitAmountHiddenInput?.innerText,
    total_amount: getFormattedCurrencyValueFromEl(formTotalAmountHiddenInput)
  });

  // Get client payment information from form
  const getClientPaymentFormData = (paymentMethodId) => ({
    name: formNameInput.value,
    email: getLowerCaseInputValue(formEmailInput),
    document: formDocumentInput.value,
    phone_number: formPhoneNumberInput.value,
    zip_code: formZipCodeInput?.value ?? "",
    coupon_id: formCouponIdHiddenInput.value,
    amount_off: formAmountOffHiddenInput.value,
    percent_off: formPercentOffHiddenInput.value,
    discount_amount: formDiscountAmountHiddenInput.value,
    amount: formAmountHiddenInput.value,
    total_amount: formTotalAmountHiddenInput.value,
    product_id: productIdHiddenInput.value,
    whatsapp_accept: whatsappAcceptElement.checked,
    installments_count: 1, // formCountInstallmentsHiddenInput.value,
    payment_method_id: paymentMethodId,
    is_foreigner: isForeignerInput?.checked,
    provider: productProviderHiddenInput.value,
    is_pj: formHelpers.pjMode
  });

  const paymentHelpers = getPaymentInfoHelpers();

  gtm.sendBeginCheckout({
    productId: productIdHiddenInput.value,
    productName: productNameHiddenInput.value,
    price: paymentHelpers.getTotalAmount(),
    coupon: formCouponIdHiddenInput.value,
    discount: paymentHelpers.getDiscountAmount(),
    fullName: formNameInput.value,
    email: formEmailInput.value,
    phone: formPhoneNumberInput.value
  });

  const gtmRegisterCheckoutOptionEvent = () => {
    const eventData = {
      price: paymentHelpers.getTotalAmount(),
      discount: paymentHelpers.getDiscountAmount(),
      coupon: formCouponIdHiddenInput.value,
      productName: productNameHiddenInput.value,
      productId: productIdHiddenInput.value,
      totalAmount: paymentHelpers.getTotalAmount(),
      fullName: formNameInput.value,
      email: formEmailInput.value,
      phone: formPhoneNumberInput.value
    };

    gtm.sendEvent(CHECKOUT_STEP.add_payment_info, eventData, PAYMENT_TYPE.pix);
  };

  const gtmRegisterPurchaseEvent = async (clientInstantPaymentResponseBody, marketingEventIdentifier) => {
    // The payment succeeded!
    const { product_id, id: transactionId, coupon_id, product_name } =
      clientInstantPaymentResponseBody;

    return gtm.sendEvent(
      CHECKOUT_STEP.purchase,
      {
        transactionId, // Transaction ID. Required for purchases and refunds.
        coupon: coupon_id,
        productId: product_id,
        productName: product_name,
        price: paymentHelpers.getTotalAmount(),
        discount: paymentHelpers.getDiscountAmount(),
        totalAmount: paymentHelpers.getTotalAmount(),
        fullName: formNameInput.value,
        email: formEmailInput.value,
        phone: formPhoneNumberInput.value
      },
      PAYMENT_TYPE.pix,
      purchaseCallbackFor(marketingEventIdentifier),
    );
  };

  /**
   * Send partial filled form data to backend as a paymentIntent
   */
  const createPaymentIntentOnDB = async () => {
    const url = window.PM3.paymentIntentsPath;

    const body = {
      payment_intent: {
        id: paymentIntentId,
        name: formNameInput.value,
        email: getLowerCaseInputValue(formEmailInput),
        product_name: productNameHiddenInput.value,
        product_id: productIdHiddenInput.value,
        total_amount: formTotalAmountHiddenInput.value,
        phone_number: formPhoneNumberInput.value,
        utm: getUTMParams()
      }
    };
    const response = await webhook.sendPost(url, body);

    if (response.ok) {
      paymentIntentId = response.body.id;
    }
  };

  // Sets email field to trigger a Create Payment Intent on lost of focus
  formEmailInput.addEventListener("blur", () => {
    if (formEmailInput.value.trim().length) createPaymentIntentOnDB();
  });

  // Sets phone number field to trigger a Create Payment Intent on lost of focus
  formPhoneNumberInput.addEventListener("blur", () => {
    if (formPhoneNumberInput.value.trim().length) createPaymentIntentOnDB();
  });

  // Shows a success message when the payment is complete and redirect
  const showOrderCompleteAndRedirect = () => {
    setLoading(false);
    const thankYouPageURL = document.getElementById("typ_url").value;
    const spinnerLoading = document.getElementById("spinner-loading");

    disableSubmitButtonElement(submitButtonElement);

    setTimeout(() => {
      window.parent.location.replace(thankYouPageURL);
    }, 2000);

    spinnerLoading.classList.remove("hidden");
    payNowButtonLabel.innerText = "Pagamento feito com sucesso!";
  };

  // Shows a error message when the payment is not complete
  const showOrderError = () => {
    setLoading(false);
    const spinnerLoading = document.getElementById("spinner-loading");

    disableSubmitButtonElement(submitButtonElement, true);

    setTimeout(() => {
      window.parent.location.replace(typPixError);
    }, 2000);

    spinnerLoading.classList.remove("hidden");

    payNowButtonLabel.innerText = "Código Pix expirado.";
  };

  // Shows a processing message when the payment is processing
  const showOrderProcessing = () => {
    setLoading(false);
    const spinnerLoading = document.getElementById("spinner-loading");

    disableSubmitButtonElement(submitButtonElement, true);

    setTimeout(() => {
      window.parent.location.replace(typPixProcessing);
    }, 2000);

    spinnerLoading.classList.remove("hidden");
    payNowButtonLabel.innerText = "Processando pagamento via Pix.";
  };

  function fetchTransactionStatus (clientInstantPaymentId, signal) {
    return fetch(`/client_instant_payments/${clientInstantPaymentId}`, { signal, headers: addCorrId() })
      .then(tapAndLoadCorrId)
      .then(async (response) => {
        if (response.ok) {
          const { status, error } = await response.json();
          return { status, error };
        }
        return { status: undefined, error: await response.text() };
      })
      .catch((error) => {
        return { status: undefined, error: { type: "requestError", error } };
      });
  }

  let cancelPixPaymentValidationLoop;
  let timeLeftUpdateLoop;

  function closeQrCodeDialog () {
    cancelPixPaymentValidationLoop?.();
    timeLeftUpdateLoop?.();

    const dialog = document.getElementById("pix-dialog");

    if (dialog.open) {
      dialog.close();
    }

    const amountHeader = dialog.querySelector(".amount-header");
    const img = dialog.querySelector("img.qr-code-img");
    const copyCta = dialog.querySelector("button.qr-code-data-copy-cta");
    const expirationInfoSpan = dialog.querySelector(".qr-code-expiration-info");

    img.src = "";
    copyCta.dataset.qrCode = "";
    amountHeader.textContent = "";
    expirationInfoSpan.textContent = "Vence em";
  }

  document.getElementById("pix-dialog")
    .querySelector(".close-qr-code-cta")
    .addEventListener("click", closeQrCodeDialog);

  document.getElementById("pix-dialog").addEventListener("cancel", (event) => {
    event.preventDefault();
    closeQrCodeDialog();
  });

  let copiedMessageTimeoutRef;

  document.getElementById("pix-dialog")
    .querySelector("button.qr-code-data-copy-cta")
    .addEventListener("click", async function handleCopyCtaClick () {
      const dialog = document.getElementById("pix-dialog");
      const self = dialog.querySelector("button.qr-code-data-copy-cta");
      const qrCode = self.dataset.qrCode;
      if (!navigator || !("clipboard" in navigator)) {
        throw new Error("Failed to copy Pix QR Code, Clipboard API not available");
      }
      if (!qrCode) {
        throw new Error("Tried to copy empty Pix QR Code");
      }
      await navigator.clipboard.writeText(qrCode);
      if (copiedMessageTimeoutRef) {
        clearTimeout(copiedMessageTimeoutRef);
      }
      const label = self.querySelector(".qr-code-data-copy-cta-label");
      const previousText = label.textContent;
      label.textContent = "Copiado!";
      copiedMessageTimeoutRef = setTimeout(() => {
        if (label.textContent !== previousText) {
          label.textContent = previousText;
        }
      }, 3000);
    });

  function pollTransactionConfirmation (clientInstantPaymentId, expirationTime = null) {
    if (cancelPixPaymentValidationLoop) {
      cancelPixPaymentValidationLoop();
      cancelPixPaymentValidationLoop = undefined;
    }
    let resolveAsPending;
    const controller = new AbortController();
    let timeoutId;

    const promise = new Promise((resolve, reject) => {
      const expirationDelta = expirationTime ? expirationTime * 1000 : PIX_STATUS_POLLING_TIME_LIMIT;
      const timeoutLimit = Date.now() + expirationDelta;
      resolveAsPending = () => {
        resolve("pending");
      };
      function executeRequest () {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          const expired = Date.now() > timeoutLimit;
          const { status, error } = await fetchTransactionStatus(clientInstantPaymentId, controller.signal);
          if (error) {
            promise.cancel(error);
            return reject(error);
          }
          if (status !== "pending") {
            return resolve(status);
          }
          if (expired) {
            return resolve("pending");
          }
          executeRequest();
        }, PIX_STATUS_POLLING_INTERVAL);
      }
      executeRequest();
    });
    promise.cancel = (failed) => {
      if (!failed && resolveAsPending) {
        resolveAsPending();
      }
      clearTimeout(timeoutId);
      controller.abort();
    };
    cancelPixPaymentValidationLoop = promise.cancel;
    return promise;
  }

  const expirationDateTimeFormatter = new Intl.DateTimeFormat(["pt-BR"], {
    day: "numeric",
    month: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit"
  });

  function showQrCodeDialog (clientInstantPaymentId, amount, imgSrc, dataContents, expiresIn) {
    setLoading(true);
    showPaymentError("");
    const dialog = document.getElementById("pix-dialog");
    const amountHeader = dialog.querySelector(".amount-header");
    const img = dialog.querySelector("img.qr-code-img");
    const copyCta = dialog.querySelector("button.qr-code-data-copy-cta");
    const expirationInfoSpan = dialog.querySelector(".qr-code-expiration-info");

    img.src = imgSrc;
    copyCta.dataset.qrCode = dataContents;
    amountHeader.textContent = formatterCurrency.format(amount / 100);

    const expirationTimestamp = Date.now() + (expiresIn * 1000);

    function updateExpirationTime () {
      const timeLeftInMs = expirationTimestamp - Date.now();
      const expiration = expirationDateTimeFormatter.format(expirationTimestamp);
      if (timeLeftInMs < 0) {
        timeLeftUpdateLoop?.();
        expirationInfoSpan.textContent = `Venceu em ${expiration}`;
      } else {
        expirationInfoSpan.textContent = `Vence em ${expiration} (${getDescriptiveTime(timeLeftInMs / 1000, true)})`;
      }
    }

    if (PIX_EXPIRATION_UPDATE_INTERVAL) {
      const expirationUpdateInterval = setInterval(updateExpirationTime, PIX_EXPIRATION_UPDATE_INTERVAL);
      timeLeftUpdateLoop = () => {
        clearInterval(expirationUpdateInterval);
        timeLeftUpdateLoop = undefined;
      };
    }

    updateExpirationTime();

    dialog.showModal();

    setLoading(false);

    return pollTransactionConfirmation(clientInstantPaymentId, expiresIn * 1000);
  }

  // Submit the form to creates payment intent
  const createClientInstantPayment = async () => {
    const gr_token = await useRecaptcha("pix_payment_creation");
    const body = {
      gr_action: "pix_payment_creation",
      gr_token,
      provider: "malga",
      product_id: productIdHiddenInput.value,
      payment_intent_id: paymentIntentId,
      utm: getUTMParams(), // Adicionar UTM diretamente do cookie
      client_payment: getClientPaymentFormData(),
      coupons: getAmountAndCouponInformation()
    };

    /**
     * @type {{
     *  body: {
     *  id: string;
     *  provider: string;
     *  amount: number;
     *  stripe_customer: { id: string; };
     *  stripe_payment_intent: { id: string, client_secret: string; };
     *  }
     * }}
     */
    const clientInstantPaymentResponse = await fetch(
      "/client_instant_payments",
      {
        method: "POST",
        headers: addCorrId({ 
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }),
        body: JSON.stringify(body)
      }
    )
      .then(tapAndLoadCorrId)
      .then(parseFetchResponse)
      .catch((error) => {
        console.trace({ error });

        return null;
      });

    if (!clientInstantPaymentResponse) {
      setLoading(false);

      showPaymentError(
        "Ocorreu um erro. Verifique os dados preenchidos e tente novamente em alguns minutos"
      );

      return;
    }

    const {
      body: {
        id: clientInstantPaymentId,
        amount: transactionAmount,
        pix,
        marketing_event_id: marketingEventIdentifier,
      }
    } = clientInstantPaymentResponse;

    const pixStatusUpdate = await showQrCodeDialog(
      clientInstantPaymentId,
      transactionAmount,
      pix.qrCodeImageUrl,
      pix.qrCodeData,
      pix.expiresIn
    );

    if (pixStatusUpdate === "authorized") {
      await gtmRegisterPurchaseEvent(clientInstantPaymentResponse.body, marketingEventIdentifier);

      const gr_token = await useRecaptcha("pix_payment_confirmation");
      const updateClientInstantPaymentResponse = await fetch(
        `/client_instant_payments/${clientInstantPaymentId}`,
        {
          method: "PATCH",
          headers: addCorrId({ 
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
          }),
          body: JSON.stringify({
            gr_token,
            stripe_payment_intent: { status: pixStatusUpdate },
          })
        }
      )
        .then(tapAndLoadCorrId)
        .then(parseFetchResponse)
        .catch((error) => {
          console.error(
            "Failed to update client instant payment with confirmed payment intent:",
            error
          );
          return null;
        });
      if (
        updateClientInstantPaymentResponse === null ||
      updateClientInstantPaymentResponse.status !== 200
      ) {
        setLoading(false);

        showPaymentError(
          "Ocorreu um erro ao confirmar o pagamento. Verifique os dados e tente novamente."
        );
        throw new Error(
          updateClientInstantPaymentResponse?.body ??
          "Failed to update client instant payment"
        );
      }
      showOrderCompleteAndRedirect();
    } else if (["pre_authorized", "pending"].includes(pixStatusUpdate)) {
      showOrderProcessing();
    } else {
      closeQrCodeDialog();
      showOrderError();
    }
  };

  /**
   * Does the submit, please use `debouncedHandleSubmit` instead
   *
   * @deprecated
   */
  function handleSubmit () {
    showPaymentError("");

    if (!formHelpers.cpfCnpjValidator()) {
      showPaymentError(`${isPJ(formClientType.value) ? "CNPJ" : "CPF"} inválido`);
      setLoading(false);
      return;
    }

    if (
      isPJ(formClientType.value) &&
      doesNotHaveValue(stateRegistrationInput) &&
      doesNotHaveValue(municipalRegistrationInput)
    ) {
      if (stateRegistrationAlertMessageElement) {
        stateRegistrationAlertMessageElement.className = "";
        stateRegistrationAlertMessageElement.style =
          "color:#FF5757; font-weight:600;";
      }
      showPaymentError("Verifique as informações inseridas");
      setLoading(false);
      return;
    }

    if (stateRegistrationAlertMessageElement) {
      stateRegistrationAlertMessageElement.className = "hidden";
    }
    setLoading(true);

    gtmRegisterCheckoutOptionEvent("paymentMethod");

    createClientInstantPayment()
      .catch((error) => {
        showPaymentError(error.message);
        setLoading(false);
        console.error(
          "Failed to create payment method",
          error.message,
          error?.stack ?? error
        );
      });
  }

  const debouncedHandleSubmit = debounced(handleSubmit, 1000);

  formElement.addEventListener("submit", (event) => {
    event.preventDefault();
    setLoading(true);
    debouncedHandleSubmit();
  });
});
